/**
 * Rebuilds the request of a saved run, to load it into the new-run form: same suite, arms
 * (model, effort and draft prompt text), trials, cases, and the judge prompt versions it
 * chose that were not current.
 *
 * Some things can change between runs and are not saved with a run: the suite in code,
 * the production prompt, the model list. A change that makes the run impossible blocks
 * it; a change that only makes it differ is listed in `notes`, so the reviewer knows the
 * two runs are not exact copies.
 */
import type { EvalModel, EvalRunDetail, EvalSuiteInfo, RunEvalArm, RunEvalRequest } from '../types/evalTypes';

export type RerunPlan = { ok: true; request: RunEvalRequest; notes: string[] } | { ok: false; reason: string };

/** A saved run's setup, loaded into the new-run form. */
export interface RunPreset {
  /** New for every load, so loading the same run twice resets the form. */
  key: string;
  runId: string;
  startedAt: number;
  request: RunEvalRequest;
  notes: string[];
}

export function planRerun(detail: EvalRunDetail, suite: EvalSuiteInfo | undefined, models: EvalModel[]): RerunPlan {
  const { run } = detail;
  if (!suite) {
    return { ok: false, reason: `The suite ${run.suiteId} no longer exists.` };
  }

  const arms: RunEvalArm[] = [];
  for (const arm of run.arms) {
    if (!models.some((model) => model.id === arm.model)) {
      return { ok: false, reason: `Arm ${arm.id} uses ${arm.model}, which is no longer allowed in evals.` };
    }
    const draft = arm.hasPromptOverride ? detail.prompts.find((prompt) => prompt.armId === arm.id) : undefined;
    if (arm.hasPromptOverride && !draft) {
      return { ok: false, reason: `The draft prompt of arm ${arm.id} was not saved, so it cannot run again.` };
    }
    arms.push({
      model: arm.model,
      ...(arm.effort && { effort: arm.effort }),
      ...(draft && { promptOverride: draft.prompt }),
    });
  }

  const notes: string[] = [];
  const suiteCaseIds = new Set(suite.cases.map((evalCase) => evalCase.id));
  const caseIds = run.caseIds.filter((id) => suiteCaseIds.has(id));
  const removed = run.caseIds.filter((id) => !suiteCaseIds.has(id));
  if (caseIds.length === 0) {
    return { ok: false, reason: 'None of the cases of this run are active any more.' };
  }
  if (removed.length > 0) {
    notes.push(`${removed.length} case(s) are no longer active and are left out: ${removed.join(', ')}.`);
  }

  const changed = detail.cases.filter((saved) => {
    const current = suite.cases.find((evalCase) => evalCase.id === saved.id);
    return current && current.versionId !== saved.versionId;
  });
  if (changed.length > 0) {
    notes.push(`${changed.length} case(s) have a new current version since this run, which is used: ${changed.map((c) => c.id).join(', ')}.`);
  }

  const baselineChanged = run.arms.some((arm) => !arm.hasPromptOverride && arm.promptHash !== suite.promptHash);
  if (baselineChanged) {
    notes.push(`The production prompt changed since this run (now ${suite.promptHash}). Arms without a draft prompt use the current one.`);
  }

  // A judge version the run chose on purpose is kept; one that was simply current is not,
  // so a loaded setup uses the judge prompt that is current now.
  const judgePromptVersions: Record<string, string> = {};
  for (const ref of run.judgePrompts) {
    const judge = suite.judgePrompts.find((candidate) => candidate.judgeName === ref.judgeName);
    if (!ref.wasCurrent) {
      if (judge?.versions.some((version) => version.versionId === ref.versionId && !version.archived)) {
        judgePromptVersions[ref.judgeName] = ref.versionId;
      } else {
        notes.push(`The judge prompt "${ref.judgeName}" v${ref.number} of this run is archived; the current version is used.`);
      }
    } else if (judge && judge.currentVersionId !== ref.versionId) {
      notes.push(`The current judge prompt "${ref.judgeName}" changed since this run (it used v${ref.number}). The current version is used.`);
    }
  }

  return {
    ok: true,
    notes,
    request: {
      suiteId: run.suiteId,
      arms,
      trials: run.trials,
      // All of the active cases: leave the list out, as the new-run form does.
      ...(caseIds.length !== suite.cases.length && { caseIds }),
      ...(Object.keys(judgePromptVersions).length > 0 && { judgePromptVersions }),
    },
  };
}
